import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSchemas } from "@/contexts/SchemaContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import FieldForm from "@/components/schema/FieldForm";
import ImportExport from "@/components/schema/ImportExport";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FieldInput } from "@/components/schema/FieldForm";
import { defaultsFor, estimateMinutes, fmtMeta, resolveFields } from "@/lib/schema";

const LEVELS = [1, 2, 3, 4, 5];

// "Start at a level, change any single setting for this patient." Stored in the activity's custom settings,
// shown as "Level 3 + custom". An empty box means "use the level's value".
// Per-patient options that do NOT change with the level (for example "Enable passthrough"). Always visible.
// Types come from the module definition: bool, enum (choices), number (min/max/unit), range ("min,max"), string.
function OptionsEditor({ module, extraText, onChange }) {
  const opts = module.customSettings || [];
  if (opts.length === 0) return null;
  let extra = {};
  try {
    const parsed = JSON.parse(extraText || "{}");
    extra = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_e) { return null; }
  const setVal = (key, v) => {
    const next = { ...extra };
    if (v === "" || v === undefined) delete next[key]; else next[key] = v;
    onChange(JSON.stringify(next));
  };
  const asBool = (o) => (extra[o.Key] === undefined ? (o.default === true || o.default === "true") : extra[o.Key] === true);
  return (
    <div className="rounded-lg border border-slate-200 bg-white/60 p-3" data-testid="module-options">
      <div className="text-sm font-medium text-slate-800">Options for this patient</div>
      <p className="text-xs text-slate-500 mb-2">These stay the same at every level. Anything you leave untouched uses the app's own default.</p>
      {opts.map((o) => {
        const v = extra[o.Key];
        const num = (x) => (x === "" || x === undefined ? "" : Number(x));
        const [lo, hi] = o.type === "range" ? String(v ?? o.default ?? "").split(",") : [];
        const outOfRange = o.type === "number" && v !== undefined && (v < o.min || v > o.max);
        const badRange = o.type === "range" && v !== undefined && Number(lo) > Number(hi);
        return (
          <div key={o.Key} className="py-1.5 border-t border-slate-100 first:border-t-0" data-testid={`option-${o.Key}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-800">{o.Label}</span>
              {o.type === "bool" ? (
                <Switch checked={asBool(o)} onCheckedChange={(x) => setVal(o.Key, x)} />
              ) : o.type === "enum" && o.options?.length ? (
                <select className="h-8 rounded-md border border-input bg-transparent text-xs px-2" value={v ?? ""} onChange={(e) => setVal(o.Key, e.target.value)}>
                  <option value="">App default{o.default ? ` (${o.default})` : ""}</option>
                  {o.options.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              ) : o.type === "number" ? (
                <Input type="number" className="h-8 w-28 text-xs" min={o.min} max={o.max} step={o.max - o.min <= 5 ? 0.05 : 1} value={v ?? ""}
                  placeholder={o.default !== undefined ? String(o.default) : "default"}
                  onChange={(e) => setVal(o.Key, e.target.value === "" ? "" : Number(e.target.value))} />
              ) : o.type === "range" ? (
                <div className="flex items-center gap-1">
                  <Input type="number" className="h-8 w-20 text-xs" min={o.min} max={o.max} step="0.05" value={num(lo)} placeholder="min"
                    onChange={(e) => setVal(o.Key, e.target.value === "" && (hi ?? "") === "" ? "" : `${e.target.value},${hi ?? o.max}`)} />
                  <span className="text-slate-400">to</span>
                  <Input type="number" className="h-8 w-20 text-xs" min={o.min} max={o.max} step="0.05" value={num(hi)} placeholder="max"
                    onChange={(e) => setVal(o.Key, e.target.value === "" && (lo ?? "") === "" ? "" : `${lo ?? o.min},${e.target.value}`)} />
                </div>
              ) : (
                <Input className="h-8 w-44 text-xs" value={v ?? ""} placeholder={o.default ? String(o.default) : "app default"} onChange={(e) => setVal(o.Key, e.target.value)} />
              )}
            </div>
            {(o.help || o.unit) && <div className="text-[11px] text-slate-500 mt-0.5">{o.help}{o.help && o.unit ? " " : ""}{o.unit ? `Unit: ${o.unit}.` : ""}</div>}
            {outOfRange && <div className="text-[11px] text-amber-700">Outside the allowed {o.min} to {o.max}.</div>}
            {badRange && <div className="text-[11px] text-amber-700">The first number must not be larger than the second.</div>}
          </div>
        );
      })}
    </div>
  );
}

function OverridesEditor({ module, level, extraText, onChange }) {
  let extra = {};
  try {
    const parsed = JSON.parse(extraText || "{}");
    extra = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_e) {
    return <div className="text-xs text-amber-700">The saved custom settings could not be read. An admin can fix them.</div>;
  }
  const rows = [
    ...(module.difficultyGuide || []).filter((r) => r.key).map((r) => ({ key: r.key, label: r.label, base: r.values?.[level - 1] })),
  ];
  if (rows.length === 0) return null;
  const count = rows.filter((r) => extra[r.key] !== undefined).length;
  const setVal = (key, v) => {
    const next = { ...extra };
    if (v === "" || v === undefined) delete next[key];
    else next[key] = /^-?\d+(\.\d+)?$/.test(String(v).trim()) ? Number(v) : v;
    onChange(JSON.stringify(next));
  };
  return (
    <details open={count > 0} className="rounded-lg border border-slate-200 bg-white/60" data-testid="overrides">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800">
        Customise for this patient (optional){count > 0 ? <span className="ml-2 text-xs text-purple-700">{count} changed</span> : null}
      </summary>
      <div className="px-3 pb-3">
        <p className="text-xs text-slate-500 mb-2">
          Start from Level {level} and change any single setting for this patient only. Leave a box empty to use the level's value.
          Changed settings show as "Level {level} + custom".
        </p>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500"><th className="py-1 font-medium">Setting</th><th className="py-1 font-medium">Level {level} value</th><th className="py-1 font-medium">This patient</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100" data-testid={`override-${r.key}`}>
                <td className="py-1 pr-2">{r.label}{r.option ? <span className="ml-1 text-[10px] text-slate-400">option</span> : null}</td>
                <td className="py-1 pr-2 text-slate-500">{r.option ? "-" : (r.base ?? "-")}</td>
                <td className="py-1 pr-2">
                  <Input className="h-7 text-xs" value={extra[r.key] ?? ""} placeholder={r.option ? "default" : "level value"}
                    onChange={(e) => setVal(r.key, e.target.value)} />
                </td>
                <td className="py-1">{extra[r.key] !== undefined && <button type="button" className="text-slate-400 hover:text-red-600" aria-label="Clear" onClick={() => setVal(r.key, "")}>x</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// One activity on a headset. Everything shown comes from the stored module definitions:
// the form (with defaults, so nothing looks switched off by accident), what each level means,
// and recent automatic level activity for this activity.
export default function AssignmentCard({ assignment, simulation, imgSrc, onRemove, onChanged, patientId, targetMinutes }) {
  const { user } = useAuth();
  const { modules: mdata, patient: pdata } = useSchemas();
  const moduleId = assignment.config?.ModuleId || simulation?.module_id;
  const module = (mdata?.modules || []).find((m) => m.id === moduleId);

  const fields = useMemo(() => {
    const all = resolveFields(mdata?.moduleInputs || [], []);
    return all.filter((f) => !f.onlyIfDomain || (module?.domains || []).includes(f.onlyIfDomain))
      .filter((f) => f.key !== "ModuleId")
      .filter((f) => !(module?.tbd && f.onlyIfDomain))
      .filter((f) => f.key !== "Extra");       // overrides have their own editor below
  }, [mdata, module]);
  const extraField = useMemo(() => resolveFields(mdata?.moduleInputs || [], []).find((f) => f.key === "Extra"), [mdata]);

  // stored values over the structure's defaults: an unset value is shown as its default, never as "off"
  const withDefaults = (stored) => ({ ...defaultsFor(fields, pdata), ...(stored || {}) });
  const [cfg, setCfg] = useState(withDefaults(assignment.config));
  const [dirty, setDirty] = useState(false);
  const [activity, setActivity] = useState([]);
  useEffect(() => { setCfg(withDefaults(assignment.config)); setDirty(false); }, [assignment.config, fields]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (patientId) api.get(`/patients/${patientId}/level-events`, { params: { limit: 50 } })
      .then((r) => setActivity(r.data.filter((l) => l.assignment_id === assignment.id).slice(0, 3))).catch(() => {});
  }, [patientId, assignment.id, assignment.config]);

  const set = (v) => { setCfg(v); setDirty(true); };
  const save = async () => {
    try {
      const { data } = await api.patch(`/assignments/${assignment.id}`, { config: cfg });
      toast.success(data.saved ? "Saved" : "No changes");
      setDirty(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const level = Number(cfg.Level) || 1;
  const guide = module?.difficultyGuide || [];
  const required = new Set(module?.requiredLabels || []);
  let custom = [];
  try { custom = Object.entries(JSON.parse(cfg.Extra || "{}")); } catch (_e) { /* shown as invalid by the field */ }

  // A distance override above 1 is beyond the patient's full reach (1 = full reach): warn, still allow saving
  const est = module ? estimateMinutes(module, level, Object.fromEntries(custom)) : null;
  const overreach = custom.filter(([k, v]) => typeof v === "number" && /Distance$/i.test(k) && v > 1).map(([k, v]) => `${k} = ${v}`);

  return (
    <AccordionItem value={assignment.id} data-testid={`simulation-config-accordion-${assignment.id}`}
      className="border border-purple-100 rounded-xl px-4 bg-white/70">
      <AccordionTrigger className="hover:no-underline py-3">
        <div className="flex items-center gap-4 w-full">
          {simulation?.image_url ? (
            <img src={imgSrc(simulation.image_url)} alt={simulation.name} className="h-10 w-10 rounded-md object-cover border border-slate-200" />
          ) : (
            <div className="h-10 w-10 rounded-md bg-brand-gradient grid place-items-center"><CheckCircle2 className="h-4 w-4 text-white" strokeWidth={1.75} /></div>
          )}
          <div className="text-left flex-1">
            <div className="font-medium text-slate-900">{simulation?.name || "Unknown"}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                {module?.name || simulation?.category || "general"}
              </span>
              {cfg.Enabled === false && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">hidden from patient</Badge>}
              {cfg.Auto === false && <Badge variant="outline" className="text-[10px]">level set by you</Badge>}
              {assignment.from_therapy && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-800 border-purple-200" data-testid="from-therapy">from therapy: {assignment.from_therapy.name}</Badge>}
            </div>
          </div>
          {!module?.tbd && <Badge variant="outline" className="chip-brand border-0 font-mono text-[10px]" data-testid={`level-badge-${assignment.id}`}>
            LEVEL {level}{custom.length ? " + custom" : ""}
          </Badge>}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-5">
        {!module && moduleId && <div className="text-xs text-amber-700 mb-3">The activity "{moduleId}" no longer exists in the module definitions. Ask an admin.</div>}
        {!moduleId && <div className="text-xs text-amber-700 mb-3">This simulation is not linked to an activity yet, so the console cannot show its levels, apply therapies to it or adjust its level. An admin can link it under Simulations &gt; Module.</div>}
        {module?.purpose && <p className="text-sm text-slate-600 mb-4" data-testid="module-purpose">{module.purpose}</p>}
        {est != null && (
          <div data-testid="session-estimate" className={`mb-3 rounded-md border p-2 text-xs ${targetMinutes && est > targetMinutes ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            One session at Level {level}{custom.length ? " + custom" : ""} takes about <b>{est} min</b>.
            {targetMinutes && est > targetMinutes ? ` That is longer than this patient's daily target of ${targetMinutes} min: they cannot finish it in a day's time. Lower the level, shorten the rounds, or raise the daily target on the patient page.` : ""}
          </div>
        )}

        {overreach.length > 0 && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="overreach-warning">
            Warning: {overreach.join(", ")} is above 1, which is beyond the patient's full reach (1 = full reach). The patient may not be able to complete it. You can still save.
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <FieldForm fields={fields} values={cfg} onChange={set} role={user.role} patientData={pdata} />
            {module && <OptionsEditor module={module} extraText={cfg.Extra} onChange={(t) => set({ ...cfg, Extra: t })} />}
            {module && <OverridesEditor module={module} level={level} extraText={cfg.Extra} onChange={(t) => set({ ...cfg, Extra: t })} />}
            {user.role === "admin" && extraField && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500">Admin: raw custom settings (JSON)</summary>
                <div className="mt-2"><FieldInput field={extraField} value={cfg.Extra} role={user.role} patientData={pdata} onChange={(t) => set({ ...cfg, Extra: t })} /></div>
              </details>
            )}
          </div>
          {guide.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-2">What each level means</div>
              <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden" data-testid={`level-table-${assignment.id}`}>
                <thead className="bg-purple-50/60">
                  <tr><th className="text-left px-2 py-1.5 font-medium">Setting</th>
                    {LEVELS.map((l) => <th key={l} className={`text-left px-2 py-1.5 font-medium ${l === level ? "bg-purple-200" : ""}`}>L{l}</th>)}</tr>
                </thead>
                <tbody>
                  {guide.map((r) => (
                    <tr key={r.label}>
                      <td className="px-2 py-1.5 border-t border-slate-100 font-medium">
                        {r.label}{required.has(r.label) ? <span className="ml-1 text-[10px] text-purple-700" title="The patient must collect at least this many each round">(required)</span> : null}
                      </td>
                      {LEVELS.map((l, i) => <td key={l} className={`px-2 py-1.5 border-t border-slate-100 ${l === level ? "bg-purple-100 font-semibold" : ""}`}>{r.values[i]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {custom.length > 0 && (
                <div className="mt-2 text-xs text-slate-600" data-testid="custom-list">
                  <b>Level {level} + custom:</b> {custom.map(([k, v]) => `${k} = ${typeof v === "object" ? JSON.stringify(v) : v}`).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>

        {activity.length > 0 && (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="recent-activity">
            <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-1">Recent level activity</div>
            {activity.map((l) => (
              <div key={l.id} className="text-xs text-slate-700"><span className="text-slate-400 tabular-nums">{new Date(l.at).toLocaleDateString()}</span> · {l.message}</div>
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3 justify-between">
          <div className="text-xs text-slate-500" data-testid={`config-meta-${assignment.id}`}>{fmtMeta(assignment.config?.Meta)}</div>
          <div className="flex flex-wrap gap-2 items-center">
            <ImportExport
              showTransfer={user.role === "admin"}
              exportUrl={`/assignments/${assignment.id}/export`}
              filename={`module-${moduleId || "config"}.json`}
              importCall={({ dryRun, allowOlder }, json) =>
                api.post(`/assignments/${assignment.id}/import`, json, { params: { dry_run: dryRun, allow_older: allowOlder } })}
              history={{ kind: "assignment_config", recordId: assignment.id }}
              onChanged={onChanged}
            />
            <Button onClick={save} disabled={!dirty} className="btn-brand rounded-full" data-testid={`save-assignment-${assignment.id}`}>
              <Save className="h-4 w-4 mr-2" /> Save
            </Button>
            <Button variant="ghost" onClick={() => onRemove(assignment.id, simulation?.name)} data-testid={`remove-assignment-${assignment.id}`}
              className="text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
